"use client";

import type { Id } from "@convex/_generated/dataModel";
import { isRealEmail } from "@convex/helpers/syntheticEmail.lib";
import { COMPANY } from "@/config/company";
import { useOptionalComposeEmail } from "@/lib/contexts/compose-email-context";
import { cn } from "@/lib/utils";
import { EmptyValue } from "@/components/ui/record-table/empty-value";

export interface EmailAddressAttribution {
  toName?: string;
  companyId?: Id<"companies">;
  contactId?: Id<"contacts">;
  leadId?: Id<"leads">;
  orderId?: Id<"orders">;
  quoteId?: Id<"quotes">;
  preferredFromAddress?: string;
  variables?: Record<string, string>;
}

export function EmailAddress({
  email,
  className,
  preferredFromAddress = COMPANY.EMAILS.SALES,
  ...attribution
}: EmailAddressAttribution & {
  email: string | undefined | null;
  className?: string;
}) {
  const compose = useOptionalComposeEmail();
  const trimmed = email?.trim();

  if (!trimmed) {
    return <EmptyValue />;
  }

  if (compose === null || !isRealEmail(trimmed)) {
    return (
      <span className={cn("max-w-full truncate text-muted-foreground", className)}>
        {trimmed}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={`Send email to ${trimmed}`}
      className={cn(
        "max-w-full truncate text-left text-link hover:text-link-hover hover:underline focus:underline focus:outline-none",
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        compose.openCompose({
          toEmail: trimmed,
          preferredFromAddress,
          restrictToSharedMailboxes: true,
          ...attribution,
        });
      }}
    >
      {trimmed}
    </button>
  );
}
